import React from "react";
import { Menu, X, User } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { SOCIAL_ICONS } from "@/components/site/socialIcons";
import { LangSwitch, useScrolled } from "../shared/TemplateShell";
import { NavMore, useInlineNavSplit } from "../shared/InlineNav";
import { useSiteContent } from "../shared/useSiteData";

/**
 * Luxe / Elegant — the theme's CHROME (header + footer), in one place.
 *
 * Extracted from LuxeTemplate so the homepage and the interior pages
 * (templates/ThemedPageLayout.jsx) render the SAME two components (BUG-006 / BUG-032).
 *
 * Idiom: the SPLIT nav — same-page anchors to the left of the centred gold wordmark,
 * interior pages to its right, everything in letterspaced Montserrat micro-caps that fade
 * in over the hero and gain a glass backdrop on scroll. The mobile panel is a dark column
 * behind a gold hairline, opened from the left, which is the convention this kind of site
 * borrows from print.
 */

export const LUXE_MAIN_CLASS = "min-h-screen bg-[var(--tpl-bg)] text-[var(--tpl-text)]";

/** The theme's letterspaced label face — every micro-caps string uses it. */
const ACCENT_FONT = { fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.25em" };
const GOLD_HAIRLINE = "rgba(212,175,55,0.2)";

const LINK = "hover:text-[var(--tpl-accent)] transition-colors whitespace-nowrap";

/**
 * The FR/EN/ES switch: the selected language is a FILLED chip, never a colour change alone.
 *
 * Gold-on-navy was already legible here, so this is not a contrast fix — it is the same
 * treatment as the other four themes, so that "which language am I reading" is answered the same
 * way across the product. Filling gold and flipping the type to the page ground is exactly what
 * this theme's buttons do on hover (see the Book CTA below).
 */
const LANG_ACTIVE = "bg-[var(--tpl-accent)] text-[var(--tpl-bg)]";
const LANG_IDLE = "opacity-50 hover:opacity-100";

/**
 * The centred wordmark, SIZED TO ITS OWN LENGTH.
 *
 * This header is a three-column flex row: two `flex-1` nav halves either side of the brand.
 * The halves therefore take the leftover width, and the brand only gets what is left over —
 * which for a long practice name ("Émilie Houde Naturopathe", 24 characters) was ~210px, so
 * it WRAPPED to three lines and made the header three times as tall as the theme intends
 * (browser-QA s11-024-t6-luxe-approach.png). A wordmark is one line by definition; the
 * variable a theme can honestly trade is its SIZE, not its line count.
 *
 * So: length tiers down through the theme's own display sizes and drops the wide tracking
 * once the name is long (letterspacing costs ~0.25 characters of width per character at
 * this scale, which is exactly the wrong thing to pay for on the longest names), plus
 * `truncate` as the floor — a 60-character name ellipsizes on one line rather than
 * reflowing the header, and `title` keeps the whole string reachable.
 */
function brandClass(brand) {
  const n = (brand || "").length;
  if (n <= 14) return "text-2xl md:text-3xl tracking-wide";
  if (n <= 22) return "text-xl md:text-2xl tracking-wide";
  if (n <= 34) return "text-base md:text-lg tracking-normal";
  return "text-sm md:text-base tracking-normal";
}

/**
 * How many of the ten nav links this theme can draw inline (BUG-045 — see
 * shared/InlineNav.jsx for why a fixed count, and why the SECTIONS are what folds).
 *
 * This theme is the one that draws its nav in TWO halves — kept sections in the left `flex-1`
 * half, kept pages in the right one, the wordmark centred between them — so the split is
 * computed once over the whole model and the halves render their own share of `inline`. The
 * single "More" disclosure at the end of the right half holds everything folded.
 *
 * The budget: 10px uppercase Montserrat labels with `gap-6` (24px) cost ~84px each, and the
 * right half also carries the account glyph, the language chips and the bordered CTA.
 * Measured with all ten links, in French, on a 24-character practice name: the bar overflowed
 * by 205px at every width from 1280px to 1536px and the CTA sat 70px past the right edge of
 * the screen. Removing k links frees ~84k against a "More" pill costing ~60, so k>=4 — six
 * inline leaves margin, and the wordmark stops being squeezed to nothing.
 *
 * The desktop nav only exists at `xl` and above here, so only the `xl` number is on screen.
 */
const INLINE_CAPACITY = { lg: 3, xl: 6 };

export const LuxeHeader = ({ toggles = {} }) => {
  const c = useSiteContent(toggles);
  const tt = useT("templates");
  const nav = c.navModel;
  // One split over the whole model — pages before sections, like every other chrome — drawn
  // back into this theme's two halves: kept sections on the left of the wordmark, kept pages
  // on its right, and everything folded in the one disclosure at the end of the right half.
  const { sectionsInline, pagesInline, overflow } = useInlineNavSplit(nav, INLINE_CAPACITY);
  const [open, setOpen] = React.useState(false);
  const scrolled = useScrolled();

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled ? "backdrop-blur-xl border-b" : ""}`}
      style={{ background: scrolled ? "rgba(15,23,42,0.7)" : "transparent", borderColor: GOLD_HAIRLINE }}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-6">
        <button
          className="xl:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label={open ? tt("shared.nav.closeMenu") : tt("shared.nav.openMenu")}
        >
          {open ? <X /> : <Menu />}
        </button>

        <nav className="hidden xl:flex items-center gap-6 flex-1 text-[10px] uppercase" style={ACCENT_FONT}>
          {sectionsInline.map((l) => (
            <a key={l.key} href={l.href} data-testid={`nav-link-${l.key}`} className={LINK}>{l.label}</a>
          ))}
        </nav>

        <a
          href={nav.home}
          className={`tpl-head font-semibold text-center min-w-0 truncate ${brandClass(nav.brand)}`}
          data-testid="logo-home"
          title={nav.brand}
          style={{ color: "var(--tpl-accent)" }}
        >
          {nav.brand}
        </a>

        <div className="hidden xl:flex items-center gap-6 flex-1 justify-end text-[10px] uppercase" style={ACCENT_FONT}>
          {pagesInline.map((l) => (
            <a key={l.key} href={l.href} data-testid={`nav-link-${l.key}`} className={LINK}>{l.label}</a>
          ))}
          <NavMore
            items={overflow}
            className={`text-[10px] uppercase ${LINK}`}
            menuClassName="border text-[10px] uppercase"
            menuStyle={{ borderColor: GOLD_HAIRLINE, background: "rgba(15,23,42,0.97)" }}
            itemClassName={LINK}
            align="right"
            iconSize={12}
          />
          {nav.account && (
            <a href={nav.account.href} data-testid="nav-client-portal" aria-label={nav.account.label} title={nav.account.label} className="text-white/70 hover:text-[var(--tpl-accent)] transition-colors">
              <User size={15} />
            </a>
          )}
          <LangSwitch lang={c.lang} setLang={c.setLang} activeClass={LANG_ACTIVE} idleClass={LANG_IDLE} />
          {nav.book && (
            // `whitespace-nowrap shrink-0`: the CTA is a BUTTON, and its label
            // ("Book a call", 0.25em tracking) was breaking after every word into a
            // three-line box taller than the header it sits in. It keeps its own width and
            // the flexible nav halves absorb the pressure instead.
            <a href={nav.book.href} data-testid="nav-book-cta" className="inline-flex items-center whitespace-nowrap shrink-0 px-5 py-2.5 border hover:bg-[var(--tpl-accent)] hover:text-[var(--tpl-bg)] transition-colors duration-500" style={{ borderColor: "var(--tpl-accent)", color: "var(--tpl-accent)" }}>{nav.book.label}</a>
          )}
        </div>

        {/* Keeps the wordmark optically centred while the two nav halves are collapsed. */}
        <span className="w-6 xl:hidden" aria-hidden="true" />
      </div>

      {open && (
        <div className="xl:hidden px-6 py-6 border-t" style={{ ...ACCENT_FONT, borderColor: GOLD_HAIRLINE, background: "rgba(15,23,42,0.95)" }} data-testid="mobile-menu">
          <div className="flex flex-col gap-4 text-xs uppercase">
            {[...nav.sections, ...nav.pages].map((l) => (
              <a key={l.key} href={l.href} onClick={() => setOpen(false)} className={LINK}>{l.label}</a>
            ))}
            {nav.account && (
              <a href={nav.account.href} onClick={() => setOpen(false)} className="inline-flex items-center gap-2">
                <User size={14} /> {nav.account.label}
              </a>
            )}
          </div>
          <div className="mt-7 pt-5 border-t flex items-center justify-between gap-4" style={{ borderColor: GOLD_HAIRLINE }}>
            <LangSwitch lang={c.lang} setLang={c.setLang} activeClass={LANG_ACTIVE} idleClass={LANG_IDLE} />
            {nav.book && (
              <a href={nav.book.href} onClick={() => setOpen(false)} className="inline-flex items-center whitespace-nowrap shrink-0 px-6 py-3 text-[10px] uppercase border" style={{ borderColor: "var(--tpl-accent)", color: "var(--tpl-accent)" }}>{nav.book.label}</a>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

/**
 * Was the brand name and "rights reserved", nothing else (BUG-026). The gold hairline and
 * the centred restraint stay; the sitemap is now actually there, set in the letterspaced
 * micro-caps the theme uses for every label.
 */
export const LuxeFooter = ({ toggles = {} }) => {
  const f = useSiteContent(toggles).footerModel;

  return (
    <footer className="px-6 py-16 text-center" data-testid="site-footer">
      <div className="max-w-4xl mx-auto">
        <div className="h-px mx-auto mb-10" style={{ background: "linear-gradient(90deg, transparent, var(--tpl-accent), transparent)" }} />
        <div className="tpl-head text-3xl" style={{ color: "var(--tpl-accent)" }}>{f.brand}</div>
        {/* BUG-210: no dictionary sample any more — an unset tagline is a fact the
            practice hasn't stated, not filler text. */}
        {f.tagline && <p className="mt-3 text-sm text-white/45">{f.tagline}</p>}

        {f.sections.length > 0 && (
          <nav className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[10px] uppercase text-white/60" style={ACCENT_FONT}>
            {f.sections.map((s) => (
              <a key={s.key} href={s.href} data-testid={`footer-section-${s.key}`} className="hover:text-[var(--tpl-accent)] transition-colors">{s.label}</a>
            ))}
          </nav>
        )}
        {f.pages.length > 0 && (
          <nav className="mt-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[10px] uppercase text-white/60" style={ACCENT_FONT}>
            {f.pages.map((p) => (
              <a key={p.key} href={p.href} data-testid={`footer-page-${p.key}`} className="hover:text-[var(--tpl-accent)] transition-colors">{p.label}</a>
            ))}
          </nav>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[10px] uppercase text-white/50" style={ACCENT_FONT}>
          <a href={`mailto:${f.contact.email}`} className="hover:text-[var(--tpl-accent)] transition-colors break-all">{f.contact.email}</a>
          <span>{f.contact.place}</span>
        </div>

        {f.socials.length > 0 && (
          <div className="mt-10 flex items-center justify-center gap-4">
            {f.socials.map((s) => {
              const Icon = SOCIAL_ICONS[s.key];
              return (
                <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label} data-testid={`footer-social-${s.key}`} className="w-10 h-10 rounded-full border flex items-center justify-center text-white/60 hover:text-[var(--tpl-accent)] transition-colors" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
                  <Icon size={15} />
                </a>
              );
            })}
          </div>
        )}

        <div className="mt-12 text-[10px] uppercase text-white/35" style={ACCENT_FONT} data-testid="footer-copyright">© {f.year} {f.brand} — {f.rights}</div>
      </div>
    </footer>
  );
};

/** The chrome contract templates/registry.js registers for t6_luxe. */
export const LUXE_CHROME = {
  Header: LuxeHeader,
  Footer: LuxeFooter,
  mainClass: LUXE_MAIN_CLASS,
};

export default LUXE_CHROME;
